import React, { useEffect, useRef, useState } from "react";
import IOButton from "../UI/Form/Button/IOButton";
import styled from "@emotion/styled";
import ButtonGroup from "../UI/ButtonGroup/ButtonGroup";
import ToPortal from "../etc/ToPortal";

const TintWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: ${(props) => props.zIndexOverride ?? 700000};
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background: ${({ theme }) =>
    theme.name === "light" ? "white" : theme.background.primary};
  color: ${({ theme }) => theme.text.primary};
  width: 550px;
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  border-radius: ${({ rounded }) => (rounded ? 20 : 2)}px;
`;

const Title = styled.h2`
  color: ${(props) => props.theme.text.secondary};
  text-align: center;
  margin-top: 18px;
  width: 100%;
  margin-bottom: 28px;
`;

/**
 * Confirmation hook.
 *
 * This hook accepts a confirmHandlerFn function of the action to take if the action is confirmed.
 * "confirmHandlerFn" may accept a value. When calling the setConfirming(...args) to bring up the confirmation overlay,
 * the args passed to it gets passed to "confirmHandlerFn" if the action is confirmed.
 *
 * @param confirmHandlerFn
 * @param title {string=}
 * @param message {React.ReactNode=}
 * @param rounded {boolean=}
 * @param zIndexOverride {string|number=}
 * @param options {object=}
 * @param options.confirmButtonTheme {"error"=}
 */
export default function useConfirmDelete(
  confirmHandlerFn,
  title = "Are you sure?",
  message,
  rounded,
  zIndexOverride,
  options
) {
  options ??= {};

  const portalRef = useRef(null);
  if (!portalRef.current && typeof document !== "undefined") {
    // Use `document.body` instead of `#modal-portal` because `#modal-portal`
    // is wrapped in a lower z-index stacking context in the app shell.
    portalRef.current = document.body ?? null;
  }

  const display = (...args) => (
    <ToPortal portalRef={portalRef}>
      <TintWrapper
        zIndexOverride={zIndexOverride}
        onMouseDown={(evt) => {
          // Treat clicking away as cancelling, but don't let the click leak to
          // any underlying modal overlays.
          evt.stopPropagation();
          if (evt.target === evt.currentTarget) {
            setConfirmingState(null);
          }
        }}
        onClick={(evt) => evt.stopPropagation()}
      >
        <Modal rounded={rounded} onMouseDown={(evt) => evt.stopPropagation()}>
          <div style={{ paddingBottom: "1rem" }}>
            <Title>{title}</Title>
            {message && (
              <div style={{ marginBottom: 20, padding: "0px 10px" }}>
                {message}
              </div>
            )}
            <ButtonGroup spacing={20} position="center">
              <IOButton
                cy="confirm-cancel"
                onClick={() => setConfirmingState(null)}
                cancel
              >
                Cancel
              </IOButton>
              <IOButton
                type="button"
                cy="confirm-confirm"
                onClick={() => handleConfirm(...args)}
                error={options.confirmButtonTheme === "error"}
              >
                Confirm
              </IOButton>
            </ButtonGroup>
          </div>
        </Modal>
      </TintWrapper>
    </ToPortal>
  );

  const [confirming, setConfirmingState] = useState(null);

  useEffect(() => {
    if (!confirming) {
      return;
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        e.stopPropagation();
        setConfirmingState(null);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [confirming]);

  const handleConfirm = (...args) => {
    confirmHandlerFn && confirmHandlerFn(...args);
    setConfirmingState(null);
  };

  const setConfirming = (...args) => {
    return setConfirmingState(display(...args));
  };

  return { confirming, setConfirming };
}
